<template>
  <div class="smart-table">
    <div class="grid_table_holder">
      <v-table
        :data="rowData"
        :current-page.sync="currentPage"
        :page-size="pageSize"
        :filters="filters"
        :hide-sort-icons="hideSortIcons"
        class="table_beauty"
        @totalPagesChanged="totalPages = $event"
      >
        <template #head>
          <thead>
            <tr>
              <v-th
                v-for="(col, index) in columns"
                :key="'column_' + index"
                class="th"
                :class="col.headerClass"
                :default-sort="col.defaultSort"
                :sort-key="col.name"
              >
                {{ $t(col.displayName) }}
                <i v-if="col.tooltipText" v-tooltip.right="$t(col.tooltipText)" class="fa fa-question-circle m-l-5" aria-hidden="true" />
              </v-th>
            </tr>
          </thead>
        </template>

        <template #body="{displayData}">
          <tbody>
            <tr
              v-for="(row, index) in displayData"
              :key="index"
              :row="row"
              :class="getRowClass(row, index)"
            >
              <td
                v-for="(col, colIndex) in columns"
                :key="col.key + '_' + colIndex + '_' + index"
                :class="col.cellClass"
                @click="$emit('click', { row, index, col, colIndex })"
              >
                <slot
                  :name="
                    $scopedSlots[`col:${colIndex + 1}`]
                      ? `col:${colIndex + 1}`
                      : 'cell'
                  "
                  v-bind="{
                    row,
                    index,
                    col,
                    colIndex,
                    value: col.valueFormatter
                      ? col.valueFormatter({ value: row[col.key] })
                      : row[col.key]
                  }"
                >
                  {{
                    col.valueFormatter
                      ? col.valueFormatter({ value: row[col.key] })
                      : row[col.key]
                  }}
                </slot>
              </td>
            </tr>
          </tbody>
        </template>
      </v-table>
    </div>
    <div class="pagination-min">
      <div class="text-center p-7">
        <smart-pagination
          :current-page.sync="currentPage"
          :total-pages="totalPages"
          :max-page-links="maxPageLinks"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 *
 * # SmartTable
 * ## A wrapper for v-table that simplifies implementation
 * Encapsulates reused logic and shared settings for:
 * 
 * - i18n
 * - Cell formatting
 * - Pagination
 * - Sorting
 * - Filtering
 * - Permissions / Roles
 * - Saving user settings
 * - Column visibility
 * 
 * ### Slots
 * 
 * Slots can be used to extend the table functionality and rendering.
 * 
 * #### Cell slot
 * 
 * The cell slot will override all table cells
 * 
 * #### Column slot
 * 
 * By specifying an index to the slot name, you can create slots that only affect individual columns
 *
 * 
 * @version 1.0.1
 */
export default {
  props: {
    /**
     * The records to be rendered 
     * @type {() => any[]} any of em
     */
    rowData: {
      type: Array,
      required: true
    },
    /**
     * Column definitions
     * @type {Vue.PropOptions<() => import('@ag-grid-community/core').ColDef[]>} ColDef
     */
    columnDefs: {
      type: Array,
      default: null
    },
    /**
     * @type {Vue.PropOptions<number>}
     */
    maxPageLinks: {
      type: Number,
      default: 20
    },
    /**
     * @type {Vue.PropOptions<number>}
     */
    pageSize: {
      type: Number,
      default: 20
    },

    filters: {
      type: Object,
      default: null
    },

    /**
     * @type {Vue.PropOptions<import('vue-i18n').I18nOptions>}
     */
    locale: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    currentPage: 1,
    totalPages: 1,
    hideSortIcons: true
  }),
  computed: {
    /** @type {() => (import('@ag-grid-community/core').ColDef & {class: string, name: string})} */
    columns() {
      if (this.rowData.length) {
        return Array.from(
          this.columnDefs || Object.keys(this.rowData[0]),
          k => {
            if (k?.hide) {
              return;
            }

            if (typeof k !== 'string') {
              /** @type {import('@ag-grid-community/core').ColDef} */
              const {
                field,
                headerName,
                colId,

                /** @type {("asc"|"desc"|null)[]} */
                sortingOrder,
                valueFormatter,
                headerClass,
                cellClass,
                tooltipText
              } = k;
              return {
                key: field || colId,
                displayName: headerName,
                name: field || colId,
                defaultSort: sortingOrder && sortingOrder[0],
                valueFormatter,
                headerClass,
                cellClass,
                tooltipText
              };
            }

            return {
              key: k,
              displayName: k,
              name: k
            };
          }
        ).filter(v => !!v);
      }

      return null;
    }
  },
  beforeMount() {
    this.$i18n.setLocaleMessage('en', this.locale.messages.en);
  },
  methods: {
    log() {
      console.log(...arguments);
    },
    getRowClass(_row, index) {
      return { 'bg-row-odd': index % 2 === 0, 'bg-row-even': index % 2 !== 0 };
    }
  }
};
</script>

<style lang="scss" scoped>
.nowrap {
  white-space: nowrap;
  vertical-align: baseline;
}

thead {
  white-space: nowrap;
}

.grid_table_holder {
  overflow-x: auto;
}
</style>
